import { RowType } from '@getaccept/editor-lib';
import type { Cell, Row, Section, Selection, Node } from '@getaccept/editor-lib';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { MoveDirection } from '@getaccept/lib-shared/src/enums/move-direction';
import type { Hover } from '../types';

export const useSelectionStore = defineStore('selection', () => {
  const selected = ref<Selection>({
    nodeId: '',
    cellId: '',
    rowId: '',
    sectionId: '',
    contentId: '',
    subElementType: undefined,
    subElementRowId: undefined,
    subElementColumnId: undefined,
  });

  const selectedSectionId = computed(() => selected.value?.sectionId);
  const selectedRowId = computed(() => selected.value?.rowId);
  const selectedCellId = computed(() => selected.value?.cellId);
  const selectedNodeId = computed(() => selected.value?.nodeId);
  const selectedPricingSectionId = computed(() => selected.value?.subElementSectionId);

  const moveRowOptions = ref<{ [contentId: string]: MoveDirection[] }>({});
  const selectedRowMoveOptions = computed(() => {
    const contentId = selected.value?.contentId;
    return contentId ? moveRowOptions.value[contentId] : undefined;
  });

  const setMoveRowOptions = (contentId: string, options: MoveDirection[]) => {
    moveRowOptions.value[contentId] = options;
  };

  const setSelection = (selection: Selection) => {
    selected.value = { ...selection };
  };

  const resetSelection = () => {
    selected.value = undefined;
  };

  const selectParentRow = () => {
    const { sectionId, rowId, contentId } = selected.value;
    setSelection({
      contentId,
      sectionId,
      rowId,
    });
  };

  const selectSubElementInCurrentNode = (payload: Partial<Selection> = {}) => {
    setSelection({
      contentId: selected.value.contentId,
      sectionId: selected.value.sectionId,
      rowId: selected.value.rowId,
      cellId: selected.value.cellId,
      nodeId: selected.value.nodeId,
      ...payload,
    });
  };

  const selectedSection = ref<Section>(null);
  const selectedRow = ref<Row>(null);
  const selectedCell = ref<Cell>(null);
  const selectedNode = ref<Node>(null);

  const updateSelected = (sections: Section[]) => {
    selectedSection.value = (sections || []).find(({ id }) => id === selected.value?.sectionId);
    selectedRow.value = selectedSection.value?.rows.find(({ id }) => id === selected.value.rowId);
    selectedCell.value = selectedRow.value?.cells.find(({ id }) => id === selected.value.cellId);
    selectedNode.value = selectedCell.value?.nodes.find(({ id }) => id === selected.value.nodeId);
  };

  const isSideBySideRow = computed(() => selectedRow.value?.type === RowType.SideBySide);

  const isColumnsRow = computed(() => selectedRow.value?.type === RowType.Columns);

  const isSlideShowRow = computed(() => selectedRow.value?.type === RowType.SlideShow);

  const cellType = computed(
    () => (isColumnsRow.value && selectedRow.value.cells[0].type) || undefined
  );

  const columnCount = computed(() => selectedRow.value?.columnCount);

  const selectedId = computed(() =>
    isSideBySideRow.value || isColumnsRow.value || isSlideShowRow.value
      ? selectedRowId.value
      : selectedNodeId.value
  );

  const isSelectedNodeLastInSection = computed(
    () => selectedCell.value?.nodes?.length === 1 && selectedSection.value?.rows?.length === 1
  );

  const isSelectedRowLastInSection = computed(() => selectedSection.value?.rows?.length === 1);

  const isFirstRowInSectionSelected = computed(
    () => selectedSection.value?.rows?.[0].id === selectedRowId.value
  );

  const isLastSection = ref(false);

  const setIsLastSection = (isLast: boolean) => {
    isLastSection.value = isLast;
  };

  const isLastRowInSectionSelected = computed(() => {
    const rows = selectedSection.value?.rows;
    return rows?.[rows.length - 1].id === selectedRowId.value;
  });

  const hovered = ref<Hover>({
    nodeId: '',
    rowId: '',
    subElementType: undefined,
    subElementRowId: undefined,
  });

  const setHover = (hover: Hover) => {
    hovered.value = { ...hover };
  };

  const resetHover = () => {
    hovered.value = undefined;
  };

  return {
    selected,
    hovered,
    selectedSection,
    selectedRow,
    selectedCell,
    selectedNode,
    selectedSectionId,
    selectedRowId,
    selectedCellId,
    selectedNodeId,
    selectedPricingSectionId,
    isSideBySideRow,
    isColumnsRow,
    isSlideShowRow,
    selectedId,
    isSelectedNodeLastInSection,
    isSelectedRowLastInSection,
    isFirstRowInSectionSelected,
    isLastRowInSectionSelected,
    cellType,
    columnCount,
    isLastSection,
    moveRowOptions,
    setSelection,
    resetSelection,
    setHover,
    resetHover,
    updateSelected,
    setIsLastSection,
    setMoveRowOptions,
    selectedRowMoveOptions,
    selectParentRow,
    selectSubElementInCurrentNode,
  };
});
